/**
 * Toggle — Henaket toggle switch component.
 *
 * 0.36.246 — Implementation of the Henaket toggle spec.
 *
 * Visual spec (from designer's CSS export):
 *   - 56 × 33 px capsule track, border-radius 20 px.
 *   - White circular bullet, 29 × 29 px, with 2 px gap to the track edge.
 *   - ON state track: navy family
 *       default #355C8C, hover #2A4A70, active #152538.
 *   - OFF state track: grey family
 *       default #999999, hover #747474, active #454545.
 *   - Focus: 4 px magenta ring (#BD13B8), border-radius 23 px, sits
 *     5 px outside the track edges.
 *   - Disabled: opacity 0.5, no hover/active state transitions.
 *   - Inside the bullet a tiny state indicator:
 *       ON  → 3 × 15 px filled vertical bar in the track colour ("|")
 *       OFF → 17 × 17 px outlined square (2 px border, grey)   ("□")
 *
 * Controlled component. The `checked` prop drives state; `onChange`
 * receives the next boolean value.
 *
 * Wraps a real <input type="checkbox"> for accessibility (screen
 * readers, keyboard) and form-submission compatibility. The visible
 * track + bullet are drawn with absolute-positioned spans on top.
 */
import styles from './Toggle.module.css';

export default function Toggle({
  checked = false,
  onChange,
  disabled = false,
  id,
  name,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  className = '',
}) {
  const cls = [
    styles.toggle,
    checked ? styles.on : styles.off,
    disabled ? styles.disabled : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <label className={cls}>
      <input
        type="checkbox"
        className={styles.input}
        checked={checked}
        disabled={disabled}
        id={id}
        name={name}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onChange={(e) => onChange && onChange(e.target.checked)}
      />
      <span className={styles.track} aria-hidden="true">
        <span className={styles.bullet}>
          <span className={checked ? styles.markOn : styles.markOff} />
        </span>
      </span>
    </label>
  );
}
